import { useState } from "react";
import { api, getErrorMessage } from "@/lib/seoApi";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import CodeCard from "@/components/CodeCard";
import { Wand2, Plus, Trash2, ScanLine, AlertTriangle, XCircle } from "lucide-react";
import { toast } from "sonner";

// Field labels: { en, fr }. Schema type names and option values stay as-is (schema.org).
const L = (en, fr) => ({ en, fr });

const SCHEMA_CONFIG = {
  Article: {
    description: L("News articles, blog posts, guides", "Articles de nouvelles, billets de blogue, guides"),
    fields: [
      { key: "headline", label: L("Headline", "Titre"), placeholder: "How AI Search Changes SEO" },
      { key: "description", label: L("Description", "Description"), type: "textarea", placeholder: L("Short summary of the article", "Court résumé de l'article") },
      { key: "url", label: L("Article URL", "URL de l'article"), placeholder: "https://exemple.com/article" },
      { key: "image", label: L("Image URL", "URL de l'image"), placeholder: "https://exemple.com/cover.jpg" },
      { key: "datePublished", label: L("Date published", "Date de publication"), type: "date" },
      { key: "dateModified", label: L("Date modified", "Date de modification"), type: "date" },
      { key: "authorName", label: L("Author name", "Nom de l'auteur"), placeholder: "Jeanne Tremblay" },
      { key: "authorUrl", label: L("Author URL", "URL de l'auteur"), placeholder: "https://exemple.com/a-propos" },
      { key: "publisherName", label: L("Publisher name", "Nom de l'éditeur"), placeholder: "Ma Marque" },
      { key: "publisherLogo", label: L("Publisher logo URL", "URL du logo de l'éditeur"), placeholder: "https://exemple.com/logo.png" },
    ],
  },
  FAQPage: {
    description: L("Q&A content - great for AI answer citations", "Contenu questions-réponses - idéal pour les citations IA"),
    fields: [],
    lists: [{ key: "faqs", label: L("Questions", "Questions"), addLabel: L("Add question", "Ajouter une question"), itemFields: [
      { key: "question", label: L("Question", "Question"), placeholder: L("What is llms.txt?", "Qu'est-ce que llms.txt?") },
      { key: "answer", label: L("Answer", "Réponse"), type: "textarea", placeholder: L("llms.txt is a proposed standard...", "llms.txt est un standard proposé...") },
    ]}],
  },
  Product: {
    description: L("Products with price, availability, ratings", "Produits avec prix, disponibilité, évaluations"),
    fields: [
      { key: "name", label: L("Product name", "Nom du produit"), placeholder: "Acme Widget Pro" },
      { key: "description", label: L("Description", "Description"), type: "textarea" },
      { key: "url", label: L("Product URL", "URL du produit"), placeholder: "https://exemple.com/produit" },
      { key: "image", label: L("Image URL", "URL de l'image") },
      { key: "sku", label: L("SKU", "SKU"), placeholder: "ACME-001" },
      { key: "brandName", label: L("Brand", "Marque"), placeholder: "Acme" },
      { key: "price", label: L("Price", "Prix"), placeholder: "49.99" },
      { key: "currency", label: L("Currency", "Devise"), placeholder: "CAD" },
      { key: "availability", label: L("Availability", "Disponibilité"), type: "select", options: ["InStock", "OutOfStock", "PreOrder"] },
      { key: "ratingValue", label: L("Rating value (optional)", "Note (optionnel)"), placeholder: "4.7" },
      { key: "reviewCount", label: L("Review count (optional)", "Nombre d'avis (optionnel)"), placeholder: "128" },
    ],
  },
  LocalBusiness: {
    description: L("Physical businesses with address and hours", "Entreprises physiques avec adresse et horaires"),
    fields: [
      { key: "name", label: L("Business name", "Nom de l'entreprise") },
      { key: "description", label: L("Description", "Description"), type: "textarea" },
      { key: "url", label: L("Website URL", "URL du site") },
      { key: "image", label: L("Image URL", "URL de l'image") },
      { key: "telephone", label: L("Phone", "Téléphone"), placeholder: "+1-514-555-0100" },
      { key: "priceRange", label: L("Price range", "Gamme de prix"), placeholder: "$$" },
      { key: "street", label: L("Street address", "Adresse") },
      { key: "city", label: L("City", "Ville") },
      { key: "region", label: L("State / Region", "Province / Région") },
      { key: "postalCode", label: L("Postal code", "Code postal") },
      { key: "country", label: L("Country", "Pays"), placeholder: "CA" },
      { key: "openingHours", label: L("Opening hours", "Heures d'ouverture"), placeholder: "Mo-Fr 09:00-17:00" },
    ],
  },
  Organization: {
    description: L("Companies and organizations", "Entreprises et organisations"),
    fields: [
      { key: "name", label: L("Organization name", "Nom de l'organisation") },
      { key: "url", label: L("Website URL", "URL du site") },
      { key: "logo", label: L("Logo URL", "URL du logo") },
      { key: "description", label: L("Description", "Description"), type: "textarea" },
      { key: "email", label: L("Email", "Courriel") },
      { key: "telephone", label: L("Phone", "Téléphone") },
    ],
    lists: [{ key: "sameAs", label: L("Social profiles (sameAs)", "Profils sociaux (sameAs)"), addLabel: L("Add profile URL", "Ajouter un profil"), itemFields: [
      { key: "url", label: L("Profile URL", "URL du profil"), placeholder: "https://twitter.com/mamarque" },
    ]}],
  },
  Person: {
    description: L("Personal profiles, authors, experts", "Profils personnels, auteurs, experts"),
    fields: [
      { key: "name", label: L("Full name", "Nom complet") },
      { key: "url", label: L("Profile URL", "URL du profil") },
      { key: "image", label: L("Photo URL", "URL de la photo") },
      { key: "jobTitle", label: L("Job title", "Titre du poste"), placeholder: L("Head of SEO", "Responsable SEO") },
      { key: "worksFor", label: L("Company", "Entreprise"), placeholder: "Acme Inc" },
    ],
    lists: [{ key: "sameAs", label: L("Social profiles (sameAs)", "Profils sociaux (sameAs)"), addLabel: L("Add profile URL", "Ajouter un profil"), itemFields: [
      { key: "url", label: L("Profile URL", "URL du profil"), placeholder: "https://linkedin.com/in/jeannetremblay" },
    ]}],
  },
  WebSite: {
    description: L("Site-wide identity + sitelinks search box", "Identité du site + boîte de recherche sitelinks"),
    fields: [
      { key: "name", label: L("Site name", "Nom du site") },
      { key: "url", label: L("Site URL", "URL du site"), placeholder: "https://exemple.com" },
      { key: "description", label: L("Description", "Description"), type: "textarea" },
      { key: "searchUrl", label: L("Search URL template (optional)", "Modèle d'URL de recherche (optionnel)"), placeholder: "https://exemple.com/search?q={search_term_string}" },
    ],
  },
  BreadcrumbList: {
    description: L("Navigation breadcrumbs for rich results", "Fil d'Ariane pour les résultats enrichis"),
    fields: [],
    lists: [{ key: "items", label: L("Breadcrumb items (in order)", "Éléments du fil (en ordre)"), addLabel: L("Add crumb", "Ajouter un élément"), itemFields: [
      { key: "name", label: L("Name", "Nom"), placeholder: "Blogue" },
      { key: "url", label: L("URL", "URL"), placeholder: "https://exemple.com/blogue" },
    ]}],
  },
  HowTo: {
    description: L("Step-by-step instructions", "Instructions étape par étape"),
    fields: [
      { key: "name", label: L("How-to title", "Titre du guide"), placeholder: L("How to add llms.txt to your site", "Comment ajouter llms.txt à votre site") },
      { key: "description", label: L("Description", "Description"), type: "textarea" },
      { key: "totalTime", label: L("Total time (ISO 8601, optional)", "Durée totale (ISO 8601, optionnel)"), placeholder: "PT15M" },
    ],
    lists: [{ key: "steps", label: L("Steps (in order)", "Étapes (en ordre)"), addLabel: L("Add step", "Ajouter une étape"), itemFields: [
      { key: "name", label: L("Step name", "Nom de l'étape"), placeholder: L("Create the file", "Créer le fichier") },
      { key: "text", label: L("Instruction", "Instruction"), type: "textarea", placeholder: L("Create a file named llms.txt in your site root...", "Créez un fichier llms.txt à la racine de votre site...") },
    ]}],
  },
};

const T = {
  en: {
    title: "Schema / JSON-LD Generator",
    sub: "Structured data is how AI engines understand your content. Pick a type, fill the form, paste the script into your page head.",
    details: "Schema details",
    type: "Schema type",
    selectPh: "Select...",
    noItems: (add) => `No items yet - click "${add}" to start.`,
    generate: "Generate JSON-LD",
    generating: "Generating...",
    generated: "JSON-LD generated",
    emptyOut1: "Your generated",
    emptyOut2: "block will appear here, ready to paste into your page's <head>.",
    detectorTitle: "Structured Data Detector",
    detectorSub: "Scan any URL to see what schema markup it already has.",
    scanPh: "exemple.com/page",
    scan: "Scan",
    scanning: "Scanning...",
    scanErr: "Scan failed",
    scanEmpty: "Please enter a URL to scan",
    noneFound: "No structured data found on this page. Use the generator on the left to create some.",
    blocks: (n) => `${n} JSON-LD block(s)`,
    blockTitle: (i, types) => `Block ${i}: ${types || "untyped"}`,
    invalidBlock: (i) => `Block ${i}: invalid JSON`,
    microdata: "Microdata:",
  },
  fr: {
    title: "Générateur de schéma / JSON-LD",
    sub: "Les données structurées sont la façon dont les moteurs IA comprennent votre contenu. Choisissez un type, remplissez le formulaire, collez le script dans le head de votre page.",
    details: "Détails du schéma",
    type: "Type de schéma",
    selectPh: "Choisir...",
    noItems: (add) => `Aucun élément - cliquez « ${add} » pour commencer.`,
    generate: "Générer le JSON-LD",
    generating: "Génération...",
    generated: "JSON-LD généré",
    emptyOut1: "Votre bloc",
    emptyOut2: "généré apparaîtra ici, prêt à coller dans le <head> de votre page.",
    detectorTitle: "Détecteur de données structurées",
    detectorSub: "Analysez n'importe quelle URL pour voir le balisage schéma déjà présent.",
    scanPh: "exemple.com/page",
    scan: "Analyser",
    scanning: "Analyse...",
    scanErr: "Échec de l'analyse",
    scanEmpty: "Veuillez entrer une URL à analyser",
    noneFound: "Aucune donnée structurée trouvée sur cette page. Utilisez le générateur à gauche pour en créer.",
    blocks: (n) => `${n} bloc(s) JSON-LD`,
    blockTitle: (i, types) => `Bloc ${i} : ${types || "sans type"}`,
    invalidBlock: (i) => `Bloc ${i} : JSON invalide`,
    microdata: "Microdonnées :",
  },
};

const pick = (v, lang) => (v && typeof v === "object" && v.en !== undefined ? v[lang] : v);

export default function SchemaGenerator() {
  const { lang } = useLang();
  const t = T[lang];
  const [schemaType, setSchemaType] = useState("Article");
  const [fields, setFields] = useState({});
  const [output, setOutput] = useState(null);
  const [loading, setLoading] = useState(false);

  const config = SCHEMA_CONFIG[schemaType];

  const changeType = (ty) => {
    setSchemaType(ty);
    setFields({});
    setOutput(null);
  };

  const setField = (key, value) => setFields((f) => ({ ...f, [key]: value }));

  const listValue = (key) => fields[key] || [];
  const addListItem = (key, itemFields) => {
    const empty = Object.fromEntries(itemFields.map((f) => [f.key, ""]));
    setField(key, [...listValue(key), empty]);
  };
  const setListItem = (key, idx, fieldKey, value) => {
    const list = [...listValue(key)];
    list[idx] = { ...list[idx], [fieldKey]: value };
    setField(key, list);
  };
  const removeListItem = (key, idx) => setField(key, listValue(key).filter((_, i) => i !== idx));

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/generate/schema", { schema_type: schemaType, fields });
      setOutput(data);
      toast.success(t.generated);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-8 sm:py-10">
      <div className="max-w-2xl">
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{t.title}</h1>
        <p className="mt-2 text-muted-foreground">{t.sub}</p>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Form */}
        <Card className="lg:col-span-5 h-fit">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading">{t.details}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t.type}</Label>
              <Select value={schemaType} onValueChange={changeType}>
                <SelectTrigger data-testid="schema-generator-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.keys(SCHEMA_CONFIG).map((ty) => (
                    <SelectItem key={ty} value={ty}>{ty}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{pick(config.description, lang)}</p>
            </div>

            <Separator />

            {config.fields.map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label htmlFor={`sf-${f.key}`}>{pick(f.label, lang)}</Label>
                {f.type === "textarea" ? (
                  <Textarea id={`sf-${f.key}`} rows={2} value={fields[f.key] || ""} onChange={(e) => setField(f.key, e.target.value)} placeholder={pick(f.placeholder, lang)} data-testid={`schema-field-${f.key}`} />
                ) : f.type === "select" ? (
                  <Select value={fields[f.key] || ""} onValueChange={(v) => setField(f.key, v)}>
                    <SelectTrigger data-testid={`schema-field-${f.key}`}><SelectValue placeholder={t.selectPh} /></SelectTrigger>
                    <SelectContent>
                      {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input id={`sf-${f.key}`} type={f.type === "date" ? "date" : "text"} value={fields[f.key] || ""} onChange={(e) => setField(f.key, e.target.value)} placeholder={pick(f.placeholder, lang)} data-testid={`schema-field-${f.key}`} />
                )}
              </div>
            ))}

            {(config.lists || []).map((list) => (
              <div key={list.key} className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>{pick(list.label, lang)}</Label>
                  <Button variant="outline" size="sm" onClick={() => addListItem(list.key, list.itemFields)} data-testid={`schema-add-${list.key}-button`}>
                    <Plus className="h-3.5 w-3.5 mr-1" /> {pick(list.addLabel, lang)}
                  </Button>
                </div>
                {listValue(list.key).length === 0 && (
                  <p className="text-xs text-muted-foreground border border-dashed rounded-md p-3">{t.noItems(pick(list.addLabel, lang))}</p>
                )}
                {listValue(list.key).map((item, idx) => (
                  <div key={idx} className="rounded-md border p-3 space-y-2 relative">
                    <Button variant="ghost" size="icon" className="absolute top-1 right-1 h-7 w-7 text-muted-foreground" onClick={() => removeListItem(list.key, idx)} aria-label="Remove item" data-testid={`schema-remove-${list.key}-${idx}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                    {list.itemFields.map((f) => (
                      <div key={f.key} className="space-y-1">
                        <Label className="text-xs">{pick(f.label, lang)}</Label>
                        {f.type === "textarea" ? (
                          <Textarea rows={2} value={item[f.key] || ""} onChange={(e) => setListItem(list.key, idx, f.key, e.target.value)} placeholder={pick(f.placeholder, lang)} data-testid={`schema-${list.key}-${idx}-${f.key}`} />
                        ) : (
                          <Input value={item[f.key] || ""} onChange={(e) => setListItem(list.key, idx, f.key, e.target.value)} placeholder={pick(f.placeholder, lang)} data-testid={`schema-${list.key}-${idx}-${f.key}`} />
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}

            <Button onClick={generate} disabled={loading} className="w-full" data-testid="schema-generator-generate-button">
              <Wand2 className="h-4 w-4 mr-2" />
              {loading ? t.generating : t.generate}
            </Button>
          </CardContent>
        </Card>

        {/* Output */}
        <div className="lg:col-span-7 space-y-6">
          {output ? (
            <CodeCard
              title={`${schemaType} JSON-LD`}
              language="json-ld"
              code={output.html}
              filename="schema.html"
              testIdPrefix="schema-generator-jsonld"
            />
          ) : (
            <div className="rounded-lg border border-dashed p-10 text-center" data-testid="schema-generator-empty-state">
              <p className="text-sm text-muted-foreground">
                {t.emptyOut1} <span className="font-mono text-xs">&lt;script type="application/ld+json"&gt;</span> {t.emptyOut2}
              </p>
            </div>
          )}

          <StructuredDataDetector t={t} />
        </div>
      </div>
    </div>
  );
}

const StructuredDataDetector = ({ t }) => {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const scan = async (e) => {
    e?.preventDefault();
    if (!url.trim()) {
      setError(t.scanEmpty);
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const { data } = await api.post("/detect/structured-data", { url: url.trim() });
      setResult(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-heading flex items-center gap-2">
          <ScanLine className="h-4 w-4" />
          {t.detectorTitle}
        </CardTitle>
        <p className="text-sm text-muted-foreground">{t.detectorSub}</p>
      </CardHeader>
      <CardContent>
        <form onSubmit={scan} className="flex flex-col sm:flex-row gap-3">
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder={t.scanPh} data-testid="structured-data-detector-url-input" />
          <Button type="submit" disabled={loading} className="shrink-0" data-testid="structured-data-detector-run-button">
            {loading ? t.scanning : t.scan}
          </Button>
        </form>

        {error && (
          <Alert variant="destructive" className="mt-4" data-testid="structured-data-detector-error">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t.scanErr}</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading && <Skeleton className="mt-4 h-32 w-full rounded-lg" />}

        {result && (
          <div className="mt-4 space-y-3" data-testid="structured-data-detector-results">
            {result.jsonld_count === 0 && result.microdata_types.length === 0 ? (
              <p className="text-sm text-muted-foreground border border-dashed rounded-md p-4">{t.noneFound}</p>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="secondary">{t.blocks(result.jsonld_count)}</Badge>
                  {result.all_types.map((ty) => (
                    <Badge key={ty} variant="outline" className="font-mono text-xs">{ty}</Badge>
                  ))}
                </div>
                {result.blocks.map((block, i) => (
                  <div key={i}>
                    {block.valid ? (
                      <CodeCard
                        title={t.blockTitle(i + 1, block.types.join(", "))}
                        language="json"
                        code={JSON.stringify(block.json, null, 2)}
                        filename={`schema-block-${i + 1}.json`}
                        testIdPrefix={`sd-block-${i}`}
                        maxHeight="260px"
                      />
                    ) : (
                      <Alert variant="destructive">
                        <XCircle className="h-4 w-4" />
                        <AlertTitle>{t.invalidBlock(i + 1)}</AlertTitle>
                        <AlertDescription className="font-mono text-xs break-all">{block.raw_preview}</AlertDescription>
                      </Alert>
                    )}
                  </div>
                ))}
                {result.microdata_types.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-muted-foreground">{t.microdata}</span>
                    {result.microdata_types.map((ty) => (
                      <Badge key={ty} variant="outline" className="font-mono text-[10px]">{ty}</Badge>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
